import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiFilesBarCodeService } from '../../../api/files/routes/bar-code.service';
import { ApiLocalPortLabel } from '../../../api/local/dto/port-label.dto';
import { PackingBoxDto } from '../../../api/elite/dto/packing-box.dto';

/** Body shape accepted by the standard Zebra packing-box label endpoint. */
type StandardLabelBody = ApiLocalPortLabel['printLabel']['body'];

/**
 * Set of customer-specific private-label flows supported by the service.
 * Adding a new private label is a three-step change:
 *   1. Add the new literal here.
 *   2. Register the customer name(s) / id(s) in `customerLabelRegistry`.
 *   3. Add the matching `case` inside `createLabel`.
 */
export type LabelType = 'skyjack' | 'sonus' | 'gabriel' | 'hendrickson';

/**
 * Entry of the customer → label-type lookup table.
 *
 * `customerName` is matched against the customer string that comes from
 * upstream (Netsuite customer name, internal customer id, etc.). The match
 * is case-insensitive and trims whitespace, so registering
 * `'skyjack'` will catch `'Skyjack'`, `'SKYJACK '`, `'  skyjack'`, etc.
 *
 * Multiple entries can point to the same `labelType` — that's how you
 * support several display names / ids for the same physical label format.
 */
export interface CustomerLabelEntry {
  customerName: string;
  labelType: LabelType;
}

/**
 * Discriminated input for {@link PackingBoxLabelCreatorApiService.createLabel}.
 *
 * The `labelType` discriminator determines which backend endpoint will be
 * hit and, consequently, which payload shape the caller must provide:
 *
 *   - `'skyjack'`      → SkyjackLabelDto      (sent as HTTP query params)
 *   - `'sonus'`        → SonusLabelDto        (sent as HTTP query params)
 *   - `'gabriel'`      → GabrielLabelDto      (sent as HTTP query params)
 *   - `'hendrickson'`  → HendricksonLabelDto  (sent as HTTP query params)
 *   - anything else / omitted → standard Zebra packing-box body (POST)
 *
 * Use `resolveLabelType(customerName)` to figure out which `labelType` to
 * pass when you only have the customer name/id at hand. When the elite
 * pre-flight endpoint (`ApiRouteLabelService.informationForPrivateLabel`)
 * returns `{ labelType: null, data: null }`, the caller must build a
 * `StandardLabelBody` locally and invoke `createLabel({ data: <built body> })`
 * — `createLabel` never receives `data: null` directly.
 */
export type CreateLabelArgs = (
  | { labelType: 'skyjack'; data: PackingBoxDto['skyjackLabel']['body'] }
  | { labelType: 'sonus'; data: PackingBoxDto['sonusLabel']['body'] }
  | { labelType: 'gabriel'; data: PackingBoxDto['gabrielLabel']['body'] }
  | {
      labelType: 'hendrickson';
      data: PackingBoxDto['hendricksonLabel']['body'];
    }
  | { labelType?: string | null; data: StandardLabelBody }
) & {
  /**
   * Optional user-entered quantity. When set, it OVERRIDES the `quantity`
   * field inside `data` for the customer-specific (private-label) branches
   * — that's how dialogs feed in the number the operator typed, since the
   * elite pre-flight (`ApiRouteLabelService.informationForPrivateLabel`)
   * doesn't know what the operator chose. Ignored by the default standard
   * packing-box branch, whose `data` already carries `quantity` directly.
   */
  quantity?: number;
};

/**
 * Resolves the ZPL string(s) for a packing-box label.
 *
 * The standard Zebra endpoint returns an array of ZPL strings (one per
 * sticker copy); the customer-specific private-label endpoints return a
 * single ZPL string. To keep the public contract uniform (and so consumers
 * don't have to branch on the response shape), the private-label responses
 * are wrapped into a single-element array.
 */
@Injectable({
  providedIn: 'root',
})
export class PackingBoxLabelCreatorApiService {
  private readonly fileApiCodesRouter = inject(ApiFilesBarCodeService);

  createLabel(args: CreateLabelArgs): Observable<string[]> {
    // The discriminated union below doesn't narrow `args.data` cleanly because
    // the default branch declares `labelType?: string | null`, which TypeScript
    // sees as overlapping the literal branches (`'skyjack'`, `'sonus'`, ...).
    // The runtime switch on `args.labelType` is exhaustive though, so each
    // case casts `args.data` to the expected per-branch shape.
    // When `args.quantity` is set, it overrides the `quantity` carried by
    // `data` (which came from the elite pre-flight and doesn't know the
    // operator-entered number). The spread guarantees nothing else in
    // `data` is lost. Encoded as a conditional spread so an undefined
    // top-level `quantity` doesn't clobber a legit `data.quantity`.
    const userQuantity =
      args.quantity !== undefined ? { quantity: args.quantity } : {};

    switch (args.labelType) {
      case 'skyjack':
        return this.fileApiCodesRouter
          .privateLabelSkyJackZplCode({
            ...args.data,
            date: new Date().toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            }),
            ...userQuantity,
          } as PackingBoxDto['skyjackLabel']['body'])
          .pipe(map(({ zpl }) => [zpl]));
      case 'sonus':
        return this.fileApiCodesRouter
          .privateLabelSonusZplCode({
            ...args.data,
            ...userQuantity,
          } as PackingBoxDto['sonusLabel']['body'])
          .pipe(map(({ zpl }) => [zpl]));
      case 'gabriel':
        return this.fileApiCodesRouter
          .privateLabelGabrielZplCode({
            ...args.data,
            ...userQuantity,
          } as PackingBoxDto['gabrielLabel']['body'])
          .pipe(map(({ zpl }) => [zpl]));
      case 'hendrickson':
        return this.fileApiCodesRouter
          .privateLabelHendricksonZplCode({
            ...args.data,
            ...userQuantity,
          } as PackingBoxDto['hendricksonLabel']['body'])
          .pipe(map(({ zpl }) => [zpl]));
      default:
        // Any other (or missing) `labelType` → standard packing-box ZPL flow.
        // Top-level `args.quantity` is intentionally ignored here; the
        // standard body always carries `quantity` inside `data`.
        return this.fileApiCodesRouter.identificationWorkOrderLabel(
          args.data as StandardLabelBody
        );
    }
  }
}
